import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import {
  allArticles,
  featuredArticle,
  type Article as MockArticle,
} from "@/mocks/articles";

// ============================================================
// Type unifié (compatible mock + Supabase)
// ============================================================
export interface Article {
  id: string;
  title: string;
  summary: string;
  image: string;
  entrepreneur: string;
  country: string;
  countryFlag: string;
  category: string;
  sector: string;
  source: string;
  sourceUrl: string;
  originalUrl?: string;
  date: string;
  featured?: boolean;
  views?: number;
  readTime?: number;
  status?: string;
}

// Mapper les colonnes snake_case Supabase → camelCase
function mapRow(row: Record<string, unknown>): Article {
  return {
    id: String(row.id ?? ""),
    title: String(row.title ?? ""),
    summary: String(row.summary ?? ""),
    image: String(row.image ?? ""),
    entrepreneur: String(row.entrepreneur ?? ""),
    country: String(row.country ?? ""),
    countryFlag: String(row.country_flag ?? "🌍"),
    category: String(row.category ?? ""),
    sector: String(row.sector ?? ""),
    source: String(row.source ?? ""),
    sourceUrl: String(row.source_url ?? ""),
    originalUrl: String(row.original_url ?? ""),
    date: String(row.date ?? ""),
    featured: Boolean(row.featured),
    views: Number(row.views ?? 0),
    readTime: Number(row.read_time ?? 5),
    status: String(row.status ?? "published"),
  };
}

// Mapper les mocks vers le type unifié
function mapMock(m: MockArticle): Article {
  return {
    id: m.id,
    title: m.title,
    summary: m.summary,
    image: m.image,
    entrepreneur: m.entrepreneur,
    country: m.country,
    countryFlag: m.countryFlag,
    category: m.category,
    sector: m.sector,
    source: m.source,
    sourceUrl: m.sourceUrl,
    date: m.date,
    featured: m.featured,
    views: m.views,
    readTime: m.readTime,
    status: "published",
  };
}

const isSupabaseConfigured = (): boolean => {
  const url = import.meta.env.VITE_PUBLIC_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;
  return Boolean(url && key && url !== "" && key !== "" && !url.includes("placeholder"));
};

// ============================================================
// Hook : liste d'articles avec filtres
// ============================================================
interface UseArticlesOptions {
  category?: string;
  country?: string;
  sector?: string;
  search?: string;
  limit?: number;
  featured?: boolean;
}

export function useArticles(options: UseArticlesOptions = {}) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (!isSupabaseConfigured()) {
      // Fallback mock
      let data = allArticles.map(mapMock);
      if (options.category && options.category !== "Toutes catégories") {
        data = data.filter((a) => a.category === options.category);
      }
      if (options.country && options.country !== "Tous les pays") {
        data = data.filter((a) => a.country.includes(options.country!));
      }
      if (options.sector && options.sector !== "Tous les secteurs") {
        data = data.filter((a) => a.sector === options.sector);
      }
      if (options.search) {
        const q = options.search.toLowerCase();
        data = data.filter(
          (a) =>
            a.title.toLowerCase().includes(q) ||
            a.summary.toLowerCase().includes(q) ||
            a.entrepreneur.toLowerCase().includes(q)
        );
      }
      if (options.featured !== undefined) {
        data = data.filter((a) => a.featured === options.featured);
      }
      if (options.limit) {
        data = data.slice(0, options.limit);
      }
      setArticles(data);
      setLoading(false);
      return;
    }

    try {
      let query = supabase
        .from("articles")
        .select("*")
        .eq("status", "published")
        .order("date", { ascending: false });

      if (options.category && options.category !== "Toutes catégories") {
        query = query.eq("category", options.category);
      }
      if (options.country && options.country !== "Tous les pays") {
        query = query.ilike("country", `%${options.country}%`);
      }
      if (options.sector && options.sector !== "Tous les secteurs") {
        query = query.eq("sector", options.sector);
      }
      if (options.search) {
        query = query.or(
          `title.ilike.%${options.search}%,summary.ilike.%${options.search}%,entrepreneur.ilike.%${options.search}%`
        );
      }
      if (options.featured !== undefined) {
        query = query.eq("featured", options.featured);
      }
      if (options.limit) {
        query = query.limit(options.limit);
      }

      const { data, error: supaError } = await query;

      if (supaError) throw supaError;

      const mapped = (data ?? []).map((row) =>
        mapRow(row as Record<string, unknown>)
      );

      // Si Supabase est vide, fallback mock
      if (mapped.length === 0) {
        let mockData = allArticles.map(mapMock);
        if (options.category && options.category !== "Toutes catégories") {
          mockData = mockData.filter((a) => a.category === options.category);
        }
        if (options.limit) mockData = mockData.slice(0, options.limit);
        setArticles(mockData);
      } else {
        setArticles(mapped);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur inconnue";
      setError(msg);
      // Fallback mock en cas d'erreur
      setArticles(allArticles.map(mapMock).slice(0, options.limit ?? 20));
    } finally {
      setLoading(false);
    }
  }, [
    options.category,
    options.country,
    options.sector,
    options.search,
    options.limit,
    options.featured,
  ]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  return { articles, loading, error, refetch: fetchArticles };
}

// ============================================================
// Hook : article unique par ID
// ============================================================
export function useArticle(id: string | undefined) {
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    const fetchArticle = async () => {
      setLoading(true);
      setError(null);

      if (!isSupabaseConfigured()) {
        const found = allArticles.find((a) => a.id === id);
        setArticle(found ? mapMock(found) : null);
        setLoading(false);
        return;
      }

      try {
        const { data, error: supaError } = await supabase
          .from("articles")
          .select("*")
          .eq("id", id)
          .maybeSingle();

        if (supaError) throw supaError;

        if (data) {
          setArticle(mapRow(data as Record<string, unknown>));
          // Incrémenter les vues
          await supabase.rpc("increment_article_views", { article_id: id });
        } else {
          // Fallback mock (IDs mock ne sont pas des UUID)
          const found = allArticles.find((a) => a.id === id);
          setArticle(found ? mapMock(found) : null);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Erreur inconnue";
        setError(msg);
        const found = allArticles.find((a) => a.id === id);
        setArticle(found ? mapMock(found) : null);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  return { article, loading, error };
}

// ============================================================
// Hook : article featured
// ============================================================
export function useFeaturedArticle() {
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      if (!isSupabaseConfigured()) {
        setArticle(mapMock(featuredArticle));
        setLoading(false);
        return;
      }

      try {
        const { data } = await supabase
          .from("articles")
          .select("*")
          .eq("featured", true)
          .eq("status", "published")
          .order("date", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (data) {
          setArticle(mapRow(data as Record<string, unknown>));
        } else {
          setArticle(mapMock(featuredArticle));
        }
      } catch {
        setArticle(mapMock(featuredArticle));
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, []);

  return { article, loading };
}

// ============================================================
// Hook : déclencher le scraping RSS
// ============================================================
export function useRssScraper() {
  const [scraping, setScraping] = useState(false);
  const [lastResult, setLastResult] = useState<{
    inserted: number;
    skipped: number;
    errors: string[];
    timestamp: string;
  } | null>(null);

  const triggerScrape = useCallback(async () => {
    setScraping(true);
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;
      const res = await fetch(`${supabaseUrl}/functions/v1/rss-scraper`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${supabaseKey}`,
          "Content-Type": "application/json",
        },
      });
      const result = await res.json();
      setLastResult(result);
      return result;
    } catch (err) {
      console.error("Scraping error:", err);
      return null;
    } finally {
      setScraping(false);
    }
  }, []);

  return { triggerScrape, scraping, lastResult };
}
